import {
  benefits,
  brands,
  categories,
  footerBenefits,
  footerColumns,
  heroSlides
} from "../../data/marketplaceData";
import type { MarketplaceState, Product } from "../../types/marketplace";
import type { MarketplaceAction } from "../actions/marketplace.actions";
import * as constants from "../constants/marketplace.constants";

const replaceProduct = (items: Product[], product: Product): Product[] =>
  items.map((item) => {
    if (product.id && item.id === product.id) {
      return product;
    }

    if (!product.id && item.name === product.name) {
      return product;
    }

    return item;
  });

const removeProduct = (items: Product[], id: string): Product[] =>
  items.filter((item) => item.id !== id);

const getOfferProducts = (items: Product[]): Product[] => {
  const offers = items.filter((item) => item.discount || item.full);

  return (offers.length > 0 ? offers : items).slice(0, 8);
};

const getBestSellers = (items: Product[]): Product[] =>
  [...items].sort((first, second) => (second.stock ?? 0) - (first.stock ?? 0)).slice(0, 10);

const emptyPaginationMeta = {
  currentPage: 1,
  lastPage: 1,
  perPage: 50,
  total: 0
};

const initialState: MarketplaceState = {
  bestSellers: [],
  benefits,
  brandCategories: [],
  brandProducts: [],
  brandProductsLoading: false,
  brandProductsMeta: emptyPaginationMeta,
  brands,
  categories,
  deleting: false,
  error: null,
  footerBenefits,
  footerColumns,
  heroSlides,
  loading: false,
  mutationError: null,
  offers: [],
  products: [],
  productsLoading: false,
  productsMeta: emptyPaginationMeta,
  saving: false,
  searchResults: [],
  searching: false
};

const marketplaceReducer = (
  state: MarketplaceState = initialState,
  action: MarketplaceAction
): MarketplaceState => {
  switch (action.type) {
    case constants.MARKETPLACE_HOME_REQUESTED:
      return {
        ...state,
        error: null,
        loading: true
      };

    case constants.MARKETPLACE_HOME_SUCCEEDED:
      return {
        ...state,
        ...action.data,
        bestSellers: action.data.bestSellers ?? getBestSellers(action.data.products),
        error: null,
        loading: false,
        offers: action.data.offers ?? getOfferProducts(action.data.products),
        productsMeta: action.data.productsMeta
      };

    case constants.MARKETPLACE_HOME_FAILED:
      return {
        ...state,
        error: action.error,
        loading: false
      };

    case constants.MARKETPLACE_SEARCH_REQUESTED:
      return {
        ...state,
        searching: action.value.trim().length >= 3,
        searchResults: action.value.trim().length >= 3 ? state.searchResults : []
      };

    case constants.MARKETPLACE_SEARCH_SUCCEEDED:
      return {
        ...state,
        searchResults: action.data,
        searching: false
      };

    case constants.MARKETPLACE_SEARCH_FAILED:
      return {
        ...state,
        error: action.error,
        searchResults: [],
        searching: false
      };

    case constants.MARKETPLACE_SEARCH_CLEARED:
      return {
        ...state,
        searchResults: [],
        searching: false
      };

    case constants.MARKETPLACE_PRODUCTS_PAGE_REQUESTED:
      return {
        ...state,
        error: null,
        productsLoading: true
      };

    case constants.MARKETPLACE_PRODUCTS_PAGE_SUCCEEDED:
      return {
        ...state,
        products: action.data.items,
        productsLoading: false,
        productsMeta: action.data.meta
      };

    case constants.MARKETPLACE_PRODUCTS_PAGE_FAILED:
      return {
        ...state,
        error: action.error,
        productsLoading: false
      };

    case constants.MARKETPLACE_BRAND_PRODUCTS_REQUESTED:
      return {
        ...state,
        brandCategories: [],
        brandProducts: [],
        brandProductsLoading: true,
        brandProductsMeta: emptyPaginationMeta,
        error: null
      };

    case constants.MARKETPLACE_BRAND_PRODUCTS_SUCCEEDED:
      return {
        ...state,
        brandCategories: action.data.categories,
        brandProducts: action.data.items,
        brandProductsLoading: false,
        brandProductsMeta: action.data.meta
      };

    case constants.MARKETPLACE_BRAND_PRODUCTS_FAILED:
      return {
        ...state,
        brandProducts: [],
        brandProductsLoading: false,
        error: action.error
      };

    case constants.PRODUCT_CREATE_REQUESTED:
    case constants.PRODUCT_UPDATE_REQUESTED:
    case constants.PRODUCT_PATCH_REQUESTED:
      return {
        ...state,
        mutationError: null,
        saving: true
      };

    case constants.PRODUCT_CREATE_SUCCEEDED:
      return {
        ...state,
        products: [action.data, ...state.products],
        saving: false
      };

    case constants.PRODUCT_UPDATE_SUCCEEDED:
    case constants.PRODUCT_PATCH_SUCCEEDED:
      return {
        ...state,
        bestSellers: replaceProduct(state.bestSellers, action.data),
        offers: replaceProduct(state.offers, action.data),
        products: replaceProduct(state.products, action.data),
        saving: false
      };

    case constants.PRODUCT_CREATE_FAILED:
    case constants.PRODUCT_UPDATE_FAILED:
    case constants.PRODUCT_PATCH_FAILED:
      return {
        ...state,
        mutationError: action.error,
        saving: false
      };

    case constants.PRODUCT_DELETE_REQUESTED:
      return {
        ...state,
        deleting: true,
        mutationError: null
      };

    case constants.PRODUCT_DELETE_SUCCEEDED:
      return {
        ...state,
        bestSellers: removeProduct(state.bestSellers, action.id),
        deleting: false,
        offers: removeProduct(state.offers, action.id),
        products: removeProduct(state.products, action.id)
      };

    case constants.PRODUCT_DELETE_FAILED:
      return {
        ...state,
        deleting: false,
        mutationError: action.error
      };

    default:
      return state;
  }
};

export default marketplaceReducer;
